import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import thLocale from "@fullcalendar/core/locales/th";
import { FiCalendar, FiClock } from "react-icons/fi";

import { fetchBookingsByDateRange } from "../../services/meetingRoomApi";
import { formatBookerLabel } from "./bookingDisplay";
import "./WeeklyBookingCalendar.css";

const pad2 = (value) => String(value).padStart(2, "0");

const toDateKey = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const getBooker = (booking) => formatBookerLabel(booking);

const getTopic = (booking) =>
  booking?.meetingTopic ||
  booking?.topic ||
  booking?.meetingTitle ||
  booking?.purpose ||
  "-";

const WeeklyBookingCalendar = ({
  selectedRoom,
  selectedDate,
  onSelectDate,
  onBookingsChange,
  onLoadingChange,
  refreshKey = 0,
}) => {
  const calendarRef = useRef(null);
  const requestRef = useRef(0);
  const [events, setEvents] = useState([]);
  const [visibleRange, setVisibleRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const roomId = selectedRoom?._id || selectedRoom?.id;

  const loadVisibleWeek = useCallback(async () => {
    if (!roomId || !visibleRange) {
      setEvents([]);
      onBookingsChange?.([]);
      onLoadingChange?.(false);
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    try {
      setLoading(true);
      onLoadingChange?.(true);
      setError("");

      const startDate = toDateKey(visibleRange.start);
      const endDateValue = new Date(visibleRange.end);
      endDateValue.setDate(endDateValue.getDate() - 1);
      const endDate = toDateKey(endDateValue);

      const bookings = await fetchBookingsByDateRange({
        roomId,
        startDate,
        endDate,
      });

      const nextEvents = (bookings || []).map((booking) => {
        const bookingDateKey = booking?.dateKey || booking?.bookingDate || startDate;

        return {
          id: booking?._id || booking?.id,
          title: getTopic(booking),
          start: `${bookingDateKey}T${booking.startTime}:00`,
          end: `${bookingDateKey}T${booking.endTime}:00`,
          backgroundColor:
            booking.createdByApp === "OPTX" ? "#2563eb" : "#c026d3",
          borderColor:
            booking.createdByApp === "OPTX" ? "#fbbf24" : "#22c55e",
          extendedProps: {
            booker: getBooker(booking),
            startTime: booking.startTime,
            endTime: booking.endTime,
          },
        };
      });

      if (requestRef.current === requestId) {
        setEvents(nextEvents);
        onBookingsChange?.(bookings || []);
      }
    } catch (loadError) {
      console.error("Load weekly bookings error:", loadError);
      if (requestRef.current === requestId) {
        setError("โหลดปฏิทินรายสัปดาห์ไม่สำเร็จ");
      }
    } finally {
      if (requestRef.current === requestId) {
        setLoading(false);
        onLoadingChange?.(false);
      }
    }
  }, [onBookingsChange, onLoadingChange, roomId, visibleRange]);

  useEffect(() => {
    loadVisibleWeek();
  }, [loadVisibleWeek, refreshKey]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") loadVisibleWeek();
    }, 60_000);

    return () => clearInterval(intervalId);
  }, [loadVisibleWeek]);

  useEffect(() => {
    if (!selectedDate) return;
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const selected = new Date(`${selectedDate}T00:00:00`);
    const currentStart = calendarApi.view?.activeStart;
    const currentEnd = calendarApi.view?.activeEnd;

    if (!currentStart || selected < currentStart || selected >= currentEnd) {
      calendarApi.gotoDate(selectedDate);
    }
  }, [selectedDate]);

  const eventContent = useCallback((eventInfo) => {
    const { booker, startTime, endTime } = eventInfo.event.extendedProps;

    return (
      <div className="min-w-0 px-1 py-0.5 leading-tight">
        <div className="truncate text-[10px] font-semibold sm:text-[11px]">
          {eventInfo.event.title}
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-1 text-[9px] opacity-90 sm:text-[10px]">
          <FiClock className="shrink-0" />
          <span className="truncate">
            {startTime}-{endTime} · {booker}
          </span>
        </div>
      </div>
    );
  }, []);

  const calendarOptions = useMemo(
    () => ({
      plugins: [timeGridPlugin, interactionPlugin],
      initialView: "timeGridWeek",
      initialDate: selectedDate,
      firstDay: 1,
      locale: thLocale,
      timeZone: "local",
      height: "auto",
      expandRows: true,
      allDaySlot: false,
      nowIndicator: true,
      slotMinTime: "09:00:00",
      slotMaxTime: "18:00:00",
      slotDuration: "00:30:00",
      slotLabelInterval: "00:30:00",
      scrollTime: "09:00:00",
      dayHeaderFormat: {
        weekday: "short",
        day: "numeric",
        month: "short",
      },
      slotLabelFormat: {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      },
      headerToolbar: {
        left: "today prev,next",
        center: "title",
        right: "",
      },
      buttonText: { today: "สัปดาห์นี้" },
      events,
      eventContent,
      datesSet: ({ start, end }) => {
        setVisibleRange((current) => {
          if (
            current?.start?.getTime() === start.getTime() &&
            current?.end?.getTime() === end.getTime()
          ) {
            return current;
          }
          return { start: new Date(start), end: new Date(end) };
        });
      },
      dateClick: ({ dateStr }) => onSelectDate?.(dateStr.slice(0, 10)),
      eventDidMount: ({ event, el }) => {
        const { booker, startTime, endTime } = event.extendedProps;
        el.title = `${startTime}-${endTime} | ${event.title} | ${booker}`;
      },
    }),
    [eventContent, events, onSelectDate, selectedDate]
  );

  return (
    <section className="weekly-booking-calendar relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/30 md:p-6">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-gray-800 dark:text-slate-100">
            <FiCalendar className="text-fuchsia-500" />
            ปฏิทินการจองรายสัปดาห์
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            {selectedRoom?.name || "กรุณาเลือกห้องประชุม"}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-sm bg-fuchsia-600" />
            NEON
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-sm bg-blue-600" />
            OPTX
          </span>
        </div>
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="min-w-[720px] lg:min-w-[820px]">
          <FullCalendar ref={calendarRef} {...calendarOptions} />
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 z-20 grid place-items-center bg-white/65 text-sm text-gray-600 backdrop-blur-[1px] dark:bg-slate-950/65 dark:text-slate-300">
          กำลังโหลดปฏิทิน...
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}
    </section>
  );
};

export default WeeklyBookingCalendar;
